import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Container } from '@mui/material';
import { collection, getDocs, doc, setDoc } from 'firebase/firestore';
import { db } from './firebase.js';
import RecipeList from './RecipeList.js';
import { useTheme } from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import * as Icons from '@mui/icons-material';
import RecipeSearch from './RecipeSearch.js';
import * as styles from './styles.js';

function MyRecipes({ user, inventory, togglePossession, favorites, bookmarks, toggleFavorite, toggleBookmark, onRecipeDeleted }) {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [userRecipes, setUserRecipes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search and filtering state
  const initialSearchTerms = location.state?.searchTerms || (() => {
    const stored = localStorage.getItem('myRecipesSearchTerms');
    return stored ? JSON.parse(stored) : [];
  });
  const [searchInput, setSearchInput] = useState("");
  const [searchTerms, setSearchTerms] = useState(initialSearchTerms);
  const [debouncedSearchInput, setDebouncedSearchInput] = useState("");
  const [sortOrder, setSortOrder] = useState(() => {
    const stored = localStorage.getItem('myRecipesSortOrder');
    return stored || 'asc';
  });
  const [statusFilters, setStatusFilters] = useState(() => {
    const stored = localStorage.getItem('myRecipesStatusFilters');
    return stored ? JSON.parse(stored) : [];
  });
  const [originFilters, setOriginFilters] = useState([]);
  const [filterHeight, setFilterHeight] = useState(0);

  // Function to add a new recipe
  const handleAddRecipe = async (recipeData) => {
    if (!user) return;
    try {
      const recipeRef = doc(collection(db, `users/${user.uid}/recipes`));
      const newRecipe = {
        ...recipeData,
        id: recipeRef.id,
        created: new Date().toISOString(),
        updated: new Date().toISOString()
      };
      await setDoc(recipeRef, newRecipe);
      setUserRecipes(prevRecipes => [...prevRecipes, { ...newRecipe, origin: 'My Recipes' }]);
    } catch (error) {
      console.error('Error creating recipe:', error);
    }
  };

  // Handle recipe deletion (for compatibility with other components)
  const handleRecipeDeleted = (deletedRecipeId) => {
    setUserRecipes(prevRecipes => prevRecipes.filter(recipe => recipe.id !== deletedRecipeId));
  };

  // Handle new recipes
  const handleRecipeAdded = (newRecipe) => {
    setUserRecipes(prevRecipes => [...prevRecipes, newRecipe]);
  };

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchInput(searchInput);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // Safely store search terms
  useEffect(() => {
    if (searchTerms.length > 0) {
      localStorage.setItem('myRecipesSearchTerms', JSON.stringify(searchTerms));
    } else {
      localStorage.removeItem('myRecipesSearchTerms');
    }
  }, [searchTerms]);

  // Safely store other filters
  useEffect(() => {
    localStorage.setItem('myRecipesSortOrder', sortOrder);
  }, [sortOrder]);

  useEffect(() => {
    if (statusFilters.length > 0) {
      localStorage.setItem('myRecipesStatusFilters', JSON.stringify(statusFilters));
    } else {
      localStorage.removeItem('myRecipesStatusFilters');
    }
  }, [statusFilters]);

  // Clear the navigation state after using it
  useEffect(() => {
    if (location.state?.searchTerms) {
      const newState = { ...location.state };
      delete newState.searchTerms;
      navigate(location.pathname, { state: newState, replace: true });
    }
  }, [location.state, location.pathname, navigate]);

  // Initial fetch of recipes
  useEffect(() => {
    const fetchUserRecipes = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        const recipesRef = collection(db, `users/${user.uid}/recipes`);
        const querySnapshot = await getDocs(recipesRef);
        
        // Calculate missingCount for each recipe when fetching
        const recipes = querySnapshot.docs.map(doc => {
          const recipeData = doc.data();
          const missingCount = (recipeData.ingredients || []).reduce((count, ing) => {
            const isOwned = inventory.some(item => item.id === ing.ingredientId && item.possessed);
            return isOwned ? count : count + 1;
          }, 0);

          return {
            id: doc.id,
            ...recipeData,
            origin: 'My Recipes',
            missingCount: missingCount
          };
        });
        
        setUserRecipes(recipes);
      } catch (error) {
        console.error('Error fetching user recipes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserRecipes();
  }, [user, inventory]);

  // Filter recipes based on search terms and filters
  const filteredRecipes = userRecipes.filter(recipe => {
    // If there are search terms, check if recipe matches any of them
    if (searchTerms.length > 0) {
      const searchMatches = searchTerms.some(term => {
        const lowerTerm = term.toLowerCase();
        // Check recipe name
        if (recipe.name.toLowerCase().includes(lowerTerm)) {
          return true;
        }
        // Check ingredients
        if (recipe.ingredients && recipe.ingredients.some(ing => 
          (ing.name || '').toLowerCase().includes(lowerTerm) || 
          (ing.ingredientId || '').toLowerCase().includes(lowerTerm)
        )) {
          return true;
        }
        return false;
      });
      if (!searchMatches) return false;
    }

    // Apply status filters
    if (statusFilters.includes('favorites') && !favorites.has(recipe.id)) {
      return false;
    }
    if (statusFilters.includes('bookmarks') && !bookmarks.has(recipe.id)) {
      return false;
    }
    if (statusFilters.includes('can-make') && recipe.missingCount > 0) {
      return false;
    }
    if (statusFilters.includes('missing-1') && recipe.missingCount !== 1) {
      return false;
    }
    if (statusFilters.includes('missing-2') && recipe.missingCount !== 2) {
      return false;
    }

    return true;
  });

  // Calculate recipe name counts for display
  const nameCounts = userRecipes.reduce((acc, recipe) => {
    acc[recipe.name] = (acc[recipe.name] || 0) + 1;
    return acc;
  }, {});

  return (
    <Box sx={{ 
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
      minHeight: '100%',
      display: 'flex',
      flexDirection: 'column',
      pt: {
        xs: `${Math.max(filterHeight, 64)}px`,
        sm: `${Math.max(filterHeight, 80)}px`,
      },
    }}>
      <Box sx={styles.fixedHeader}>
        <Container maxWidth="md" disableGutters sx={styles.container}>
          <RecipeSearch
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            searchTerms={searchTerms}
            setSearchTerms={setSearchTerms}
            debouncedSearchInput={debouncedSearchInput}
            statusFilters={statusFilters}
            setStatusFilters={setStatusFilters}
            originFilters={originFilters}
            setOriginFilters={setOriginFilters}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            navigate={navigate}
            originFilter="My Recipes"
            uniqueOrigins={[]}
            recipes={userRecipes}
            inventory={inventory}
            onFilterHeightChange={setFilterHeight}
          />
        </Container>
      </Box>
      
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        px: { xs: 2, sm: 3 },
        mb: 2
      }}>
        <Button
          component={Link}
          to="/recipe/new"
          variant="contained"
          startIcon={<Icons.Add />}
        >
          Add Recipe
        </Button>
      </Box>
      
      {loading ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Loading recipes...
          </Typography>
        </Box>
      ) : filteredRecipes.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {userRecipes.length === 0 ? 
              "You haven't created any recipes yet." : 
              "No recipes match your search criteria."}
          </Typography>
        </Box>
      ) : (
        <RecipeList
          filteredRecipes={filteredRecipes}
          inventory={inventory}
          favorites={favorites}
          bookmarks={bookmarks}
          toggleFavorite={toggleFavorite}
          toggleBookmark={toggleBookmark}
          nameCounts={nameCounts}
          searchTerms={searchTerms}
          sortOrder={sortOrder}
          from={{ type: 'collection', id: 'my-recipes' }}
        />
      )}
    </Box>
  );
}

export default MyRecipes; 